/**
 * QR Code Creator — design, preview, save.
 * Screen code: QR-002
 * After save → auto-redirect to /qr-codes/manage.
 */
import { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Save, Download, Upload, X, Loader2, QrCode, Palette,
  Link as LinkIcon, Layers,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API = process.env.REACT_APP_BACKEND_URL;

// Presets exposed in the "Platform" dropdown — every choice suggests a base URL,
// which the admin can freely override.
const PLATFORM_PRESETS = [
  { key: 'cloudgm_business', ar: 'CloudGM Business (منصة التجار)', en: 'CloudGM Business (Merchants)', base: 'https://cloudgm.business' },
  { key: 'cloudgm_market',   ar: 'CloudGM Market (منصة العملاء)', en: 'CloudGM Market (Customers)',   base: 'https://cloudgm.com'      },
  { key: '606_fitness',      ar: '606 Fitness',                    en: '606 Fitness',                   base: 'https://fitness.606.ae'  },
  { key: '606_community',    ar: '606 Community',                  en: '606 Community',                 base: 'https://community.606.ae'},
  { key: '606_mediadocs',    ar: '606 MediaDocs',                  en: '606 MediaDocs',                 base: 'https://mediadocs.606.ae'},
  { key: 'cgm_coin',         ar: 'CGM Coin',                       en: 'CGM Coin',                      base: 'https://coin.cloudgm.com'},
];

const ERROR_LEVELS = [
  { key: 'L', ar: 'منخفض (~7% تصحيح)',  en: 'Low (~7%)'    },
  { key: 'M', ar: 'متوسط (~15% تصحيح)', en: 'Medium (~15%)' },
  { key: 'Q', ar: 'مرتفع (~25% تصحيح)', en: 'Quartile (~25%)' },
  { key: 'H', ar: 'عالي (~30% تصحيح)',  en: 'High (~30%)'   },
];

// Visual style of every "dot" (module) in the QR
const QR_STYLES = [
  // Feb 2026 — "beautiful" is the new default: circular dots + rounded-square
  // eyes, matching the design samples the customer shared (bitcoin/wifi/etc).
  { key: 'beautiful', ar: '🎨 التصميم الجميل (النقاط الدائرية)', en: '🎨 Beautiful (rounded dots + eyes)' },
  { key: 'square',    ar: 'كلاسيكي (مربعات حادة)',    en: 'Classic (sharp squares)' },
  { key: 'rounded',   ar: 'ناعم (مربعات دائرية)',    en: 'Rounded (soft blobs)'   },
  { key: 'circle',    ar: 'نقاط دائرية',              en: 'Dots (circles)'         },
  { key: 'gapped',    ar: 'متباعد (فراغات بين النقاط)', en: 'Gapped (spaced squares)' },
  { key: 'bars_v',    ar: 'خطوط عمودية',              en: 'Vertical bars'          },
  { key: 'bars_h',    ar: 'خطوط أفقية',               en: 'Horizontal bars'        },
];

export default function QRCodeCreator() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const canvasWrapRef = useRef(null);
  const logoInputRef = useRef(null);

  const [label, setLabel]         = useState('');
  const [platform, setPlatform]   = useState('cloudgm_business');
  const [page, setPage]           = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [design, setDesign] = useState({
    fg_color: '#0a0a0a',
    bg_color: '#ffffff',
    // Feb 2026 — bumped default EC to 'H' so the "beautiful" style
    // (circular dots) stays scannable even when a logo covers ~22% of the
    // centre. Recovery: L=7% M=15% Q=25% H=30%.
    error_correction: 'H',
    box_size: 10,
    border: 4,
    style: 'beautiful',
    logo_data_url: null,
    logo_size_pct: 22,
  });
  const [saving, setSaving]           = useState(false);
  const [downloading, setDown]        = useState(false);
  // Server-rendered preview (data URL) — kept in sync with design via debounce
  const [previewUrl, setPreviewUrl]   = useState('');
  const [previewLoading, setPreviewL] = useState(false);

  // Auto-suggest target URL when platform + page change and user hasn't
  // manually typed one yet.
  useEffect(() => {
    const preset = PLATFORM_PRESETS.find(p => p.key === platform);
    if (!preset || !preset.base) return;
    // Only auto-fill if the field is empty or currently matches an old preset base
    const isOldPreset = PLATFORM_PRESETS.some(p => p.base && targetUrl.startsWith(p.base));
    if (!targetUrl || isOldPreset) {
      const suffix = page ? (page.startsWith('/') ? page : `/${page}`) : '';
      setTargetUrl(preset.base + suffix);
    }
  }, [platform, page]);

  const updateDesign = (patch) => setDesign(d => ({ ...d, ...patch }));

  // Debounced server-side preview render — guarantees the preview matches
  // exactly what will be saved & downloaded (including style + logo size).
  useEffect(() => {
    if (!targetUrl) { setPreviewUrl(''); return; }
    let cancel = false;
    setPreviewL(true);
    const handle = setTimeout(async () => {
      try {
        const res = await axios.post(
          `${API}/api/admin/qr-codes/preview`,
          { data: targetUrl, design },
          { headers: getAuthHeaders() },
        );
        if (!cancel) setPreviewUrl(res.data.data_url);
      } catch {
        if (!cancel) setPreviewUrl('');
      } finally {
        if (!cancel) setPreviewL(false);
      }
    }, 350);
    return () => { cancel = true; clearTimeout(handle); };
  }, [targetUrl, design]);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('حجم الشعار أكبر من 2 ميجابايت', 'Logo bigger than 2 MB'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => updateDesign({ logo_data_url: reader.result });
    reader.readAsDataURL(file);
  };

  // Download server-rendered preview directly (WYSIWYG).
  const downloadLocal = () => {
    if (!previewUrl) {
      toast.error(t('انتظر انتهاء المعاينة', 'Wait for the preview to finish'));
      return;
    }
    setDown(true);
    try {
      const a = document.createElement('a');
      a.href = previewUrl;
      a.download = `${(label || 'qr-code').replace(/\s+/g, '_')}.png`;
      a.click();
      toast.success(t('تم تنزيل الصورة', 'Image downloaded'));
    } catch (err) {
      toast.error(t('فشل التنزيل', 'Download failed') + ': ' + err.message);
    } finally {
      setDown(false);
    }
  };

  const save = async () => {
    if (!label.trim()) {
      toast.error(t('أدخل اسم الرمز', 'Enter a label'));
      return;
    }
    if (!targetUrl.trim() || !/^https?:\/\//i.test(targetUrl.trim())) {
      toast.error(t('الرابط يجب أن يبدأ بـ http:// أو https://', 'URL must start with http:// or https://'));
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/admin/qr-codes`,
        {
          label: label.trim(),
          platform,
          page: page.trim(),
          target_url: targetUrl.trim(),
          design,
        },
        { headers: getAuthHeaders() },
      );
      toast.success(t('✓ تم الحفظ', '✓ Saved'));
      navigate('/qr-codes/manage');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      toast.error(t('فشل الحفظ', 'Save failed') + ': ' + msg);
    } finally {
      setSaving(false);
    }
  };

  const platformOptions = useMemo(() => PLATFORM_PRESETS, []);

  const breadcrumb = [
    { labelAr: 'لوحة التحكم',   labelEn: 'Dashboard',       path: '/dashboard'      },
    { labelAr: 'نظام QR Code',  labelEn: 'QR Code System',  path: '/qr-codes'       },
    { labelAr: 'إنشاء جديد',    labelEn: 'New'                                       },
  ];

  return (
    <DashboardLayout
      title="إنشاء QR Code جديد"
      titleEn="Create New QR Code"
      subtitle="اختر المنصة، اضبط الرابط، خصّص التصميم، ثم احفظ."
      subtitleEn="Pick the platform, set the URL, customize the design, then save."
      screenCode="QR-002"
      backPath="/qr-codes"
      onBack={() => navigate('/qr-codes')}
      breadcrumb={breadcrumb}
    >
      <section
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
        dir={isAr ? 'rtl' : 'ltr'}
        data-testid="qr-creator-root"
      >
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* ── Form (3 cols) ── */}
          <div className="lg:col-span-3 space-y-5">
            {/* Basic */}
            <Card icon={LinkIcon} title={t('المعلومات الأساسية', 'Basic Info')}>
              <Row label={t('اسم / وسم الرمز', 'Label / Tag')} required>
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder={t('مثال: كتالوج رمضان 2026', 'e.g. Ramadan 2026 catalogue')}
                  className="bg-[#050505] border-white/10 text-white"
                  data-testid="qr-label-input"
                />
              </Row>

              <Row label={t('المنصة', 'Platform')} required>
                <select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="w-full h-10 px-3 rounded-md bg-[#050505] border border-white/10 text-white text-sm"
                  data-testid="qr-platform-select"
                >
                  {platformOptions.map(p => (
                    <option key={p.key} value={p.key} className="bg-[#050505]">
                      {t(p.ar, p.en)}
                    </option>
                  ))}
                </select>
              </Row>

              <Row label={t('الصفحة (اختياري)', 'Page (optional)')}>
                <Input
                  value={page}
                  onChange={(e) => setPage(e.target.value)}
                  placeholder={t('/product/abc  أو  landing', '/product/abc or landing')}
                  className="bg-[#050505] border-white/10 text-white"
                  data-testid="qr-page-input"
                />
              </Row>

              <Row label={t('الرابط النهائي (Target URL)', 'Target URL')} required>
                <Input
                  value={targetUrl}
                  onChange={(e) => setTargetUrl(e.target.value)}
                  placeholder="https://…"
                  className="bg-[#050505] border-white/10 text-white font-mono text-xs"
                  data-testid="qr-target-url-input"
                />
                <p className="text-[10px] text-white/40 mt-1">
                  {t(
                    'عند مسح الـ QR سيوجّه المستخدم إلى هذا الرابط، مع تسجيل عملية المسح للإحصاء.',
                    'On scan, users are redirected to this URL and the scan is logged for stats.',
                  )}
                </p>
              </Row>
            </Card>

            {/* Design */}
            <Card icon={Palette} title={t('التصميم', 'Design')}>
              <div className="grid grid-cols-2 gap-4">
                <ColorField
                  label={t('لون الرمز', 'Foreground')}
                  value={design.fg_color}
                  onChange={(v) => updateDesign({ fg_color: v })}
                  testid="qr-fg-color"
                />
                <ColorField
                  label={t('لون الخلفية', 'Background')}
                  value={design.bg_color}
                  onChange={(v) => updateDesign({ bg_color: v })}
                  testid="qr-bg-color"
                />
              </div>

              <Row label={t('نمط الرمز', 'QR Style')}>
                <select
                  value={design.style}
                  onChange={(e) => updateDesign({ style: e.target.value })}
                  className="w-full h-10 px-3 rounded-md bg-[#050505] border border-white/10 text-white text-sm"
                  data-testid="qr-style-select"
                >
                  {QR_STYLES.map(s => (
                    <option key={s.key} value={s.key} className="bg-[#050505]">
                      {t(s.ar, s.en)}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-white/40 mt-1">
                  {t(
                    'كل نمط يعيد رسم النقاط بشكل مختلف — الشكل النهائي يظهر في المعاينة على اليمين.',
                    'Each style redraws the modules differently — the final look appears in the preview panel.',
                  )}
                </p>
              </Row>

              <Row label={t('مستوى تصحيح الأخطاء', 'Error correction')}>
                <select
                  value={design.error_correction}
                  onChange={(e) => updateDesign({ error_correction: e.target.value })}
                  className="w-full h-10 px-3 rounded-md bg-[#050505] border border-white/10 text-white text-sm"
                  data-testid="qr-ec-select"
                >
                  {ERROR_LEVELS.map(l => (
                    <option key={l.key} value={l.key} className="bg-[#050505]">
                      {l.key} — {t(l.ar, l.en)}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-white/40 mt-1">
                  {t(
                    'أعلى مستوى → رمز أكثر تعقيداً لكنه يقاوم الخدوش والحدود.',
                    'Higher level → busier code, more resilient to scratches / cropping.',
                  )}
                </p>
              </Row>

              <Row label={t('كثافة النقاط (Box size)', 'Dot density (Box size)')}>
                <input
                  type="range"
                  min={4}
                  max={20}
                  step={1}
                  value={design.box_size}
                  onChange={(e) => updateDesign({ box_size: Number(e.target.value) })}
                  className="w-full accent-[#d4af37]"
                  data-testid="qr-box-size"
                />
                <div className="text-[10px] text-white/40">{design.box_size}px</div>
              </Row>
            </Card>

            {/* Logo */}
            <Card icon={Layers} title={t('الشعار في المنتصف (اختياري)', 'Center Logo (optional)')}>
              <div className="flex items-center gap-4">
                {design.logo_data_url ? (
                  <div className="relative w-20 h-20 rounded-xl overflow-hidden bg-white p-1 border border-white/10">
                    <img
                      src={design.logo_data_url}
                      alt="logo"
                      className="w-full h-full object-contain"
                    />
                    <button
                      type="button"
                      onClick={() => updateDesign({ logo_data_url: null })}
                      className="absolute -top-1 -right-1 p-1 rounded-full bg-red-500 text-white hover:bg-red-400"
                      data-testid="qr-remove-logo"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-white/5 border border-dashed border-white/20 flex items-center justify-center text-white/40 text-[10px] text-center px-1">
                    {t('لا يوجد شعار', 'No logo')}
                  </div>
                )}

                <div className="flex-1">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleLogoUpload}
                    data-testid="qr-logo-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="border-white/10 text-white/80 hover:bg-white/5 gap-2"
                    onClick={() => logoInputRef.current?.click()}
                    data-testid="qr-upload-logo-btn"
                  >
                    <Upload className="w-4 h-4" />
                    {t('رفع شعار', 'Upload logo')}
                  </Button>
                  <p className="text-[10px] text-white/40 mt-2">
                    {t(
                      'استخدم PNG شفاف مربع. يوصى بمستوى تصحيح "عالي" عند إضافة شعار.',
                      'Use a square transparent PNG. Bump error correction to "High" when using a logo.',
                    )}
                  </p>
                </div>
              </div>

              {/* Logo size slider — only visible when a logo is uploaded */}
              {design.logo_data_url && (
                <Row label={t('حجم الشعار داخل الرمز', 'Logo size inside QR')}>
                  <input
                    type="range"
                    min={10}
                    max={40}
                    step={1}
                    value={design.logo_size_pct}
                    onChange={(e) => updateDesign({ logo_size_pct: Number(e.target.value) })}
                    className="w-full accent-[#d4af37]"
                    data-testid="qr-logo-size"
                  />
                  <div className="flex justify-between text-[10px] text-white/40 mt-1">
                    <span>{t('أصغر', 'Smaller')}</span>
                    <span className="text-[#d4af37] font-bold">{design.logo_size_pct}%</span>
                    <span>{t('أكبر', 'Larger')}</span>
                  </div>
                </Row>
              )}
            </Card>
          </div>

          {/* ── Live preview (2 cols) ── */}
          <aside className="lg:col-span-2">
            <div className="sticky top-6">
              <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-4">
                  <QrCode className="w-4 h-4 text-[#d4af37]" />
                  <h3 className="text-white font-bold text-sm">
                    {t('معاينة مباشرة', 'Live preview')}
                  </h3>
                </div>

                <div
                  ref={canvasWrapRef}
                  className="mx-auto p-4 rounded-xl flex items-center justify-center relative"
                  style={{ backgroundColor: design.bg_color, minHeight: 280 }}
                  data-testid="qr-preview"
                >
                  {targetUrl ? (
                    previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="QR preview"
                        style={{ width: 260, height: 260, objectFit: 'contain' }}
                      />
                    ) : (
                      <div className="h-[260px] w-[260px] flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-white/30 animate-spin" />
                      </div>
                    )
                  ) : (
                    <div className="h-[260px] w-[260px] flex items-center justify-center text-white/30 text-xs text-center px-6">
                      {t('أدخل رابط لتظهر المعاينة', 'Enter a URL to see the preview')}
                    </div>
                  )}
                  {previewLoading && targetUrl && previewUrl && (
                    <div className="absolute top-2 end-2 flex items-center gap-1 bg-black/50 backdrop-blur px-2 py-0.5 rounded-full">
                      <Loader2 className="w-3 h-3 text-white/60 animate-spin" />
                      <span className="text-[9px] text-white/60">{t('تحديث', 'updating')}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="mt-5 space-y-2">
                  <Button
                    onClick={downloadLocal}
                    disabled={downloading || !targetUrl}
                    className="w-full bg-white/10 hover:bg-white/15 text-white gap-2"
                    variant="outline"
                    data-testid="qr-download-btn"
                  >
                    {downloading
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Download className="w-4 h-4" />}
                    {t('تنزيل معاينة PNG', 'Download preview PNG')}
                  </Button>

                  <Button
                    onClick={save}
                    disabled={saving || !label || !targetUrl}
                    className="w-full bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold gap-2"
                    data-testid="qr-save-btn"
                  >
                    {saving
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Save className="w-4 h-4" />}
                    {t('حفظ', 'Save')}
                  </Button>
                </div>

                <p className="text-[10px] text-white/40 text-center mt-3">
                  {t(
                    'الحفظ يُنشئ رمزاً قابلاً للتتبّع (إحصائيات مسح).',
                    'Saving creates a trackable code (scan analytics).',
                  )}
                </p>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </DashboardLayout>
  );
}

// ─── Small helpers ─────────────────────────────────────────
function Card({ icon: Icon, title, children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 space-y-4"
    >
      <div className="flex items-center gap-2 pb-2 border-b border-white/5">
        <Icon className="w-4 h-4 text-[#d4af37]" />
        <h3 className="text-white font-bold text-sm">{title}</h3>
      </div>
      {children}
    </motion.div>
  );
}

function Row({ label, required, children }) {
  return (
    <div>
      <label className="block text-white/70 text-xs mb-1.5">
        {label}
        {required && <span className="text-red-400 ms-1">*</span>}
      </label>
      {children}
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-white/70 text-xs mb-1.5">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded-md border border-white/10 bg-transparent cursor-pointer"
          data-testid={`${testid}-picker`}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-[#050505] border-white/10 text-white text-xs font-mono uppercase flex-1"
          data-testid={`${testid}-hex`}
        />
      </div>
    </div>
  );
}
